/**
 * atomberg-support-forms.js
 *
 * Generic submit handling for the Support forms and Register Warranty.
 * Binds to every [data-support-form-root], reading its
 * data-support-form-type to pick the right payload shape and endpoint:
 *
 *   register-complaint → POST /api/support/complaint  {request_type: 'service', items: [...]}
 *   installation       → POST /api/support/complaint  {request_type: 'installation', items: [...]}
 *   inspection         → POST /api/support/inspection  {...}
 *   b2b-service        → POST /api/support/complaint  {request_type: 'b2b_service', items: [{...}]}
 *   warranty           → POST /api/support/warranty  {..., products: [...]}
 *
 * Top-level fields (outside any repeatable row) are read generically by
 * their native `name` attribute — the shared snippets (phone-otp, address,
 * product-type-select) already carry one each. Fields inside a
 * [data-support-repeatable-row] have no unique `name` across rows, so
 * they're tagged with [data-field="key"] instead and collected per-row (one
 * object per item) rather than at the top level. Neither path hardcodes
 * field names in this file — each form snippet just tags its own inputs.
 *
 * Every form requires OTP verification ([data-support-otp-verified="true"]
 * within the root) before submit — this fails CLOSED (see
 * atomberg-support-otp.js's header comment for why that's different from
 * the cart's Clickpost checks).
 *
 * Two-step forms (register-complaint, installation) have a "Continue"
 * button ([data-support-form-continue]) that runs native validity + the OTP
 * gate on step 1 before revealing step 2 ([data-support-form-step="2"]); a
 * "Back" button ([data-support-form-back]) reverses it. Single-step forms
 * (inspection, b2b-service) submit directly from their one step.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergSupportFormsInit) return;
  w._atombergSupportFormsInit = true;

  /** @returns {string} */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-support-api-base-url]');
    return (root && root.getAttribute('data-support-api-base-url')) || '';
  }

  /**
   * @param {HTMLElement} scope
   * Top-level fields (outside any repeatable row) are read by their native
   * `name` attribute — the shared snippets (phone-otp, address,
   * product-type-select) already carry one each, so no extra data
   * attribute is needed for these.
   * @returns {Record<string, string>}
   */
  function collectTopLevelFields(scope) {
    /** @type {Record<string, string>} */
    var out = {};
    scope.querySelectorAll('[name]').forEach(function (el) {
      if (el.closest('[data-support-repeatable-row]')) return;
      var key = el.getAttribute('name');
      if (!key) return;
      out[key] = /** @type {HTMLInputElement} */ (el).value || '';
    });
    return out;
  }

  /**
   * @param {HTMLElement} formRoot
   * @returns {Array<Record<string, any>>}
   */
  function collectItemRows(formRoot) {
    var rows = formRoot.querySelectorAll('[data-support-repeatable-row]');
    return Array.from(rows).map(function (row) {
      /** @type {Record<string, any>} */
      var item = {};
      row.querySelectorAll('[data-field]').forEach(function (el) {
        var key = el.getAttribute('data-field');
        if (!key) return;
        item[key] = /** @type {HTMLInputElement} */ (el).value || '';
      });
      var filesInput = /** @type {HTMLInputElement|null} */ (
        row.querySelector('[data-support-row-file-json]')
      );
      if (filesInput && filesInput.value) {
        try {
          item.files = JSON.parse(filesInput.value);
        } catch (_) {
          item.files = [];
        }
      }
      return item;
    });
  }

  /**
   * @param {HTMLElement} formRoot
   * @returns {boolean}
   */
  function isOtpVerified(formRoot) {
    var otpBlock = formRoot.querySelector('[data-support-otp]');
    return !!otpBlock && otpBlock.getAttribute('data-support-otp-verified') === 'true';
  }

  /**
   * Signed identity token minted by Liquid for a signed-in customer (see
   * atomberg-support-phone-otp.liquid). The backend accepts it in place of an OTP
   * marker after re-checking the signature itself; absent, it falls back to the
   * OTP flow, so sending an empty one is harmless.
   * @param {HTMLElement} formRoot
   * @returns {string}
   */
  function getIdentityToken(formRoot) {
    var otpBlock = formRoot.querySelector('[data-support-otp]');
    return (otpBlock && otpBlock.getAttribute('data-support-identity-token')) || '';
  }

  // The phone/OTP block wraps its label and input in its own container
  // rather than .atomberg-support-field, so both count as a field.
  var FIELD_WRAP = '.atomberg-support-field, .atomberg-support-phone-otp';

  /**
   * Field's visible label text, with the required marker stripped.
   * @param {Element} el
   * @returns {string}
   */
  function fieldLabelText(el) {
    var wrap = el.closest(FIELD_WRAP);
    var label = wrap ? wrap.querySelector('.atomberg-support-field__label') : null;
    if (!label) return '';
    var clone = /** @type {HTMLElement} */ (label.cloneNode(true));
    clone.querySelectorAll('[data-support-required-marker], button').forEach(function (node) {
      node.remove();
    });
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Message for one failing field, derived from its ValidityState so no
   * per-field copy has to live in markup.
   * @param {HTMLInputElement|HTMLSelectElement} el
   * @returns {string}
   */
  function fieldErrorMessage(el) {
    var v = el.validity;
    var label = fieldLabelText(el).toLowerCase();
    if (v.valueMissing) {
      if (!label) return 'This field is required.';
      if (/** @type {HTMLInputElement} */ (el).type === 'file') return 'Upload your ' + label + '.';
      return (el.tagName === 'SELECT' ? 'Select your ' : 'Enter your ') + label + '.';
    }
    if (v.rangeOverflow) return 'Choose a date on or before today.';
    if (v.rangeUnderflow) return 'Choose a more recent date.';
    return label ? 'Enter a valid ' + label + '.' : 'Enter a valid value.';
  }

  /**
   * Error node for a field, created next to the input on first use — most
   * fields ship without one and seeding 40+ empty nodes into the snippets
   * would be worse than building them on demand.
   * @param {Element} el
   * @returns {HTMLElement|null}
   */
  function fieldErrorEl(el) {
    var wrap = el.closest(FIELD_WRAP);
    if (!wrap) return null;
    var existing = /** @type {HTMLElement|null} */ (
      wrap.querySelector('[data-support-field-error]')
    );
    if (existing) return existing;
    var node = document.createElement('p');
    node.className = 'atomberg-support-field__error';
    node.setAttribute('data-support-field-error', '');
    node.hidden = true;
    wrap.appendChild(node);
    return node;
  }

  /**
   * @param {HTMLElement} scope
   */
  function clearFieldErrors(scope) {
    scope.querySelectorAll('[data-support-field-error]').forEach(function (node) {
      var el = /** @type {HTMLElement} */ (node);
      el.textContent = '';
      el.hidden = true;
    });
    scope.querySelectorAll('[aria-invalid="true"]').forEach(function (node) {
      node.setAttribute('aria-invalid', 'false');
    });
  }

  /**
   * Writes a message beneath every failing field in scope, replacing the
   * single generic line that used to sit above the Continue button.
   * @param {HTMLElement} scope
   * @returns {boolean} true when every field passes native validity
   */
  function showFieldErrors(scope) {
    clearFieldErrors(scope);
    var first = /** @type {HTMLElement|null} */ (null);
    scope.querySelectorAll(':invalid').forEach(function (node) {
      var el = /** @type {HTMLInputElement} */ (node);
      if (el.disabled || el.type === 'hidden' || el.closest('[hidden]')) return;
      var target = fieldErrorEl(el);
      if (target) {
        target.textContent = fieldErrorMessage(el);
        target.hidden = false;
      }
      el.setAttribute('aria-invalid', 'true');
      if (!first) first = el;
    });
    if (first) first.focus();
    return !first;
  }

  /**
   * Appends the required marker to each required field's label. Done here
   * rather than in the snippets so it cannot drift from the `required`
   * attribute it describes.
   * @param {HTMLElement} root
   */
  function markRequiredLabels(root) {
    root.querySelectorAll('[required]').forEach(function (node) {
      var wrap = node.closest(FIELD_WRAP);
      var label = wrap ? wrap.querySelector('.atomberg-support-field__label') : null;
      if (!label || label.querySelector('[data-support-required-marker]')) return;
      var marker = document.createElement('span');
      marker.className = 'atomberg-support-field__required';
      marker.setAttribute('data-support-required-marker', '');
      marker.setAttribute('aria-hidden', 'true');
      marker.textContent = '*';
      label.appendChild(marker);
    });
  }

  /**
   * Clears a field's error as soon as the user edits it, so a message never
   * outlives the problem it describes.
   * @param {HTMLElement} root
   */
  function bindFieldErrorClearing(root) {
    ['input', 'change'].forEach(function (evt) {
      root.addEventListener(evt, function (e) {
        var el = /** @type {HTMLElement|null} */ (e.target);
        if (!el || !el.closest) return;
        var wrap = el.closest(FIELD_WRAP);
        if (!wrap) return;
        var node = /** @type {HTMLElement|null} */ (
          wrap.querySelector('[data-support-field-error]')
        );
        if (node && !node.hidden) {
          node.textContent = '';
          node.hidden = true;
        }
        el.setAttribute('aria-invalid', 'false');
      });
    });
  }

  // Shared with atomberg-warranty-wizard.js, which loads after this file.
  /** @type {any} */ (window).AtombergFieldErrors = {
    show: showFieldErrors,
    clear: clearFieldErrors,
  };

  var NOT_SERVICEABLE_MESSAGE =
    'Sorry for the inconvenience, we are currently not available at your location. Please check your pincode and try again.';

  /**
   * Hard FAIL-CLOSED gate for installation requests, mirroring the reference
   * site: the flow may only proceed once atomberg-support-address.js has
   * stamped data-support-installation-serviceable="true" on the form root.
   * "checking", "false", and absent (no pincode looked up yet) all block —
   * that JS also disables the Continue button; this is the backstop.
   * @param {HTMLElement} formRoot
   * @returns {string} blocking message, or '' when clear to proceed
   */
  function installationGateMessage(formRoot) {
    if (formRoot.getAttribute('data-support-form-type') !== 'installation') return '';
    var state = formRoot.getAttribute('data-support-installation-serviceable');
    if (state === 'true') return '';
    if (state === 'checking') return 'Please wait — checking serviceability for your pincode…';
    if (state === 'false') return NOT_SERVICEABLE_MESSAGE;
    return 'Please enter your pincode so we can check installation serviceability.';
  }

  /**
   * @param {HTMLElement} formRoot
   * @param {string} message
   */
  function showFormError(formRoot, message) {
    var el = /** @type {HTMLElement|null} */ (formRoot.querySelector('[data-support-form-error]'));
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /**
   * Renders message text into el with phone numbers (tel:), emails
   * (mailto:), and atomberg.com paths as real anchors. Built from text
   * nodes + <a> elements, never innerHTML — failure messages can be raw
   * Salesforce text and must not be parsed as HTML.
   * @param {Element} el
   * @param {string} message
   */
  function renderMessageWithLinks(el, message) {
    el.textContent = '';
    // Email | atomberg.com path | phone-looking run (digits with optional
    // spaces/dashes, so "8448 449 442" is one token — whitespace splitting
    // never linked formatted numbers at all).
    var linkToken =
      /[\w.+-]+@[\w-]+(?:\.[\w-]+)+|(?:https?:\/\/)?(?:www\.)?atomberg\.com(?:\/[\w\-/]*)?|\+?\d[\d\s-]{8,14}\d/g;
    var last = 0;
    var match;
    while ((match = linkToken.exec(message))) {
      var token = match[0];
      var href = '';
      if (token.indexOf('@') !== -1) {
        href = 'mailto:' + token;
      } else if (/atomberg\.com/i.test(token)) {
        href = /^https?:\/\//.test(token) ? token : 'https://' + token;
      } else {
        href = phoneHref(token);
      }
      // Numeric runs that aren't a phone (case ids) stay plain text.
      if (!href) continue;
      el.appendChild(document.createTextNode(message.slice(last, match.index)));
      var a = document.createElement('a');
      a.href = href;
      a.textContent = token;
      el.appendChild(a);
      last = match.index + token.length;
    }
    el.appendChild(document.createTextNode(message.slice(last)));
  }

  /**
   * tel: href for a run of digits/spaces/dashes, or '' when the digits don't
   * form an Indian number: 10-digit mobile ([6-9]…), 11-digit landline with
   * STD code (0…), or either with a 91 prefix. 8-digit case ids fall through.
   * @param {string} raw
   * @returns {string}
   */
  function phoneHref(raw) {
    var digits = raw.replace(/\D/g, '');
    if (digits.length === 12 && digits.indexOf('91') === 0) digits = digits.slice(2);
    if (digits.length === 10 && /^[6-9]/.test(digits)) return 'tel:+91' + digits;
    if (digits.length === 11 && digits.charAt(0) === '0') return 'tel:+91' + digits.slice(1);
    return '';
  }

  /**
   * @param {HTMLElement} formRoot
   * @param {{status: boolean, message: string, case_id?: string, results?: Array<{serial_number: string, status: boolean, warranty_id?: string, message?: string}>}} res
   * @param {{fan?: boolean}} [opts] - fan: installation items are fans — the
   *   reference sends those customers an SMS confirmation link, so their
   *   success message differs from other appliances'.
   */
  function showResult(formRoot, res, opts) {
    var resultEl = /** @type {HTMLElement|null} */ (
      formRoot.querySelector('[data-support-form-result]')
    );
    var stepsEl = formRoot.querySelectorAll('[data-support-form-step]');
    if (!resultEl) return;
    stepsEl.forEach(function (step) {
      /** @type {HTMLElement} */ (step).hidden = true;
    });
    resultEl.hidden = false;
    resultEl.classList.toggle('atomberg-support__result--error', !res.status);

    // Success copy comes from each form's data-support-success-message
    // (reference parity — every form has its own wording). A {case_id} token
    // embeds the id in the sentence and suppresses the separate ticket line.
    var template = '';
    if (res.status) {
      if (opts && opts.fan) {
        template = formRoot.getAttribute('data-support-success-message-fan') || '';
      }
      template = template || formRoot.getAttribute('data-support-success-message') || '';
    }
    if (!res.status) {
      // Salesforce's duplicate-case wording varies by form ("open service
      // repair" for installation, "already exists" for service requests) and
      // reads wrong either way — swap in the corrected copy (merchant-editable
      // attribute) whenever the failure looks like a duplicate-case rejection.
      var duplicateCopy = formRoot.getAttribute('data-support-duplicate-message') || '';
      if (
        duplicateCopy &&
        res.message &&
        /duplicat|already\s+(open|exist)|case\s+is\s+open|open\s+(service|repair|installation)/i.test(
          res.message
        )
      ) {
        res.message = duplicateCopy;
      }
      // A message on a failure is Salesforce's exact business error (duplicate
      // case, unknown model id, …) — show it verbatim. The form's generic
      // "Oops" copy + possible-causes list is only for opaque failures.
      template = res.message ? '' : formRoot.getAttribute('data-support-error-message') || '';
    }
    var caseInMessage = template.indexOf('{case_id}') !== -1;
    var message = template
      ? template.replace('{case_id}', res.case_id || '')
      : res.message || (res.status ? 'Request submitted.' : 'Something went wrong.');

    var messageEl = resultEl.querySelector('[data-support-form-result-message]');
    if (messageEl) renderMessageWithLinks(messageEl, message);

    var causesEl = /** @type {HTMLElement|null} */ (
      resultEl.querySelector('[data-support-installation-error-causes]')
    );
    if (causesEl) causesEl.hidden = res.status || !!res.message;
    var caseEl = resultEl.querySelector('[data-support-form-result-case]');
    if (caseEl) {
      var showCase = !!res.case_id && !caseInMessage;
      caseEl.textContent = showCase ? 'Ticket No. ' + res.case_id : '';
      /** @type {HTMLElement} */ (caseEl).hidden = !showCase;
    }

    var retryBtn = /** @type {HTMLElement|null} */ (
      resultEl.querySelector('[data-support-form-retry]')
    );
    if (retryBtn) retryBtn.hidden = res.status;

    // Register Warranty submits multiple serial numbers at once — the
    // backend responds with one result per product rather than a single
    // case_id, so render each line instead of just the aggregate message.
    var listEl = /** @type {HTMLElement|null} */ (
      resultEl.querySelector('[data-support-form-result-list]')
    );
    if (listEl) {
      var list = listEl;
      list.innerHTML = '';
      var results = Array.isArray(res.results) ? res.results : [];
      results.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'atomberg-support-track__case';
        var detail = item.status
          ? item.warranty_id
            ? 'Warranty ID ' + item.warranty_id
            : 'Registered'
          : item.message || 'Failed';
        li.textContent = 'Serial ' + item.serial_number + ': ' + detail;
        list.appendChild(li);
      });
      // Salesforce business failures can be per-model (e.g. a duplicate case
      // names the model and existing case number) — surface each line.
      var sfErrors =
        !res.status && Array.isArray(/** @type {any} */ (res).errors)
          ? /** @type {Array<{model_id?: string, error_message?: string, case_number?: string}>} */ (
              /** @type {any} */ (res).errors
            )
          : [];
      sfErrors.forEach(function (err) {
        var li = document.createElement('li');
        li.className = 'atomberg-support-track__case';
        li.textContent =
          (err.model_id ? err.model_id + ': ' : '') +
          (err.error_message || 'Failed') +
          (err.case_number ? ' (Case ' + err.case_number + ')' : '');
        list.appendChild(li);
      });
      list.hidden = !results.length && !sfErrors.length;
    }

    // Multi-screen flows (the warranty wizard) own their screen visibility
    // and need to know the outcome to route to success vs stay put.
    formRoot.dispatchEvent(
      new CustomEvent('atomberg:support-form-result', { detail: res, bubbles: true })
    );
  }

  /**
   * b2b-service is the one form shared across three customer types (b2b,
   * b2g, institutional) — it has no hardcoded customer_type field, unlike
   * Register Complaint/Installation which are each only ever shown for
   * "customer". Read the live value from the page-level cascade select
   * instead, falling back to 'b2b' only if that select is somehow missing.
   * @returns {string}
   */
  function resolveCustomerTypeForB2b() {
    var select = /** @type {HTMLSelectElement|null} */ (
      document.querySelector('[data-support-customer-type]')
    );
    return (select && select.value) || 'b2b';
  }

  /**
   * @param {HTMLElement} formRoot
   * @param {string} formType
   * @returns {any}
   */
  function buildPayload(formRoot, formType) {
    var top = collectTopLevelFields(formRoot);
    var items = collectItemRows(formRoot);

    if (formType === 'inspection') {
      return {
        name: top.name || '',
        poc_name: top.poc_name || '',
        mobile_number: top.mobile_number || '',
        product_type: top.product_type || '',
        product_category: top.product_category || '',
        quantity: top.quantity ? Number(top.quantity) : undefined,
        address: top.address1 || '',
        city: top.city || '',
        state: top.state || '',
        pincode: top.pincode || '',
        otp_verified: true,
        on_behalf_mobile_number: top.on_behalf_mobile_number || undefined,
        customer_token: getIdentityToken(formRoot) || undefined,
      };
    }

    if (formType === 'warranty') {
      /** @type {{name: string, base64: string, size_bytes: number}|undefined} */
      var invoice;
      try {
        var invoiceFiles = top.invoice_file_json ? JSON.parse(top.invoice_file_json) : [];
        invoice = invoiceFiles[0];
      } catch (_) {
        invoice = undefined;
      }
      return {
        name: top.name || '',
        email: top.email || '',
        mobile_number: top.mobile_number || '',
        otp_verified: isOtpVerified(formRoot),
        customer_token: getIdentityToken(formRoot) || undefined,
        purchase_date: top.purchase_date || '',
        source_of_purchase: top.source_of_purchase || '',
        source_of_purchase_other: top.source_of_purchase_other || '',
        address1: top.address1 || '',
        pincode: top.pincode || '',
        city: top.city || '',
        state: top.state || '',
        invoice: invoice,
        products: items.map(function (item) {
          return {
            serial_number: item.serial_number || '',
            model: item.model || '',
            sku: item.sku || '',
          };
        }),
      };
    }

    var requestType =
      formType === 'installation'
        ? 'installation'
        : formType === 'b2b-service'
          ? 'b2b_service'
          : 'service';
    var customerType =
      formType === 'b2b-service' ? resolveCustomerTypeForB2b() : top.customer_type || 'customer';
    // Service and installation pick ONE product type for the whole
    // submission (form-level select, like the reference site), but
    // Salesforce still expects it on every item.
    if (top.product_type) {
      items.forEach(function (item) {
        if (!item.product_type) item.product_type = top.product_type;
      });
    }
    return {
      request_type: requestType,
      customer_type: customerType,
      name: top.name || '',
      mobile_number: top.mobile_number || '',
      email: top.email || '',
      company: top.company || '',
      address1: top.address1 || '',
      address2: top.address2 || '',
      city: top.city || '',
      state: top.state || '',
      pincode: top.pincode || '',
      source_of_purchase: top.source_of_purchase || '',
      source_of_purchase_other: top.source_of_purchase_other || '',
      otp_verified: true,
      on_behalf_mobile_number: top.on_behalf_mobile_number || undefined,
      customer_token: getIdentityToken(formRoot) || undefined,
      items: items,
    };
  }

  /**
   * Reads the same data-support-max-file-size-mb attribute
   * atomberg-support-repeatable.js validates uploads against, so the 413
   * message below can name the actual configured limit instead of a
   * hardcoded number.
   * @param {HTMLElement} formRoot
   * @returns {number}
   */
  function getMaxFileSizeMb(formRoot) {
    var w = /** @type {any} */ (window);
    var limits =
      w.AtombergSupport && w.AtombergSupport.getFileLimits
        ? w.AtombergSupport.getFileLimits(formRoot)
        : null;
    return limits ? Math.round(limits.maxBytes / (1024 * 1024)) : 2;
  }

  /**
   * @param {string} formType
   * @returns {string}
   */
  function endpointFor(formType) {
    if (formType === 'inspection') return '/api/support/inspection';
    if (formType === 'warranty') return '/api/support/warranty';
    return '/api/support/complaint';
  }

  /** @param {HTMLElement} formRoot */
  function bindContinueBack(formRoot) {
    var continueBtn = formRoot.querySelector('[data-support-form-continue]');
    var backBtn = formRoot.querySelector('[data-support-form-back]');
    var step1El = /** @type {HTMLElement|null} */ (
      formRoot.querySelector('[data-support-form-step="1"]')
    );
    var step2El = /** @type {HTMLElement|null} */ (
      formRoot.querySelector('[data-support-form-step="2"]')
    );
    if (!continueBtn || !step1El || !step2El) return;
    var step1 = step1El;
    var step2 = step2El;

    continueBtn.addEventListener('click', function () {
      if (!showFieldErrors(step1)) {
        showFormError(formRoot, '');
        return;
      }
      if (!isOtpVerified(formRoot)) {
        showFormError(formRoot, 'Please verify your mobile number to continue.');
        return;
      }
      var continueGate = installationGateMessage(formRoot);
      if (continueGate) {
        showFormError(formRoot, continueGate);
        return;
      }
      showFormError(formRoot, '');
      step1.hidden = true;
      step2.hidden = false;
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        step2.hidden = true;
        step1.hidden = false;
      });
    }
  }

  /**
   * Resets every field in formRoot back to blank/default so a failed
   * submission can be retried without a page reload — EXCEPT anything
   * inside [data-support-otp], which is left untouched: OTP verification
   * has no expiry once granted (see atomberg-support-otp.js), so there is
   * no reason to make a verified user re-verify just because the actual
   * submission failed (e.g. a Salesforce duplicate-case error). Also
   * collapses repeatable rows back down to one and clears every per-field
   * error/status message left over from before.
   * @param {HTMLElement} formRoot
   */
  function resetFormFields(formRoot) {
    // Drop extra repeatable rows FIRST so the generic field pass below
    // doesn't bother resetting fields that are about to be removed anyway.
    var repeatable = formRoot.querySelector('[data-support-repeatable]');
    if (repeatable) {
      var list = repeatable.querySelector('[data-support-repeatable-list]');
      if (list) {
        list.querySelectorAll('[data-support-repeatable-row]').forEach(function (row, i) {
          if (i > 0) row.remove();
        });
      }
      repeatable.setAttribute('data-support-repeatable-invalid', 'false');
      var w = /** @type {any} */ (window);
      if (w.AtombergSupport && w.AtombergSupport.renumberRepeatable) {
        w.AtombergSupport.renumberRepeatable(repeatable);
      }
    }

    formRoot.querySelectorAll('input, select, textarea').forEach(function (field) {
      if (field.closest('[data-support-otp]')) return;
      var input = /** @type {HTMLInputElement} */ (field);
      if (field.tagName === 'SELECT') {
        /** @type {HTMLSelectElement} */ (field).selectedIndex = 0;
      } else if (input.type === 'hidden') {
        // Only dynamic hidden fields carry data worth clearing (serial
        // lookup's sku, encoded file JSON) — static ones like the
        // customer_type marker stay exactly as Liquid rendered them.
        var isDynamicHidden =
          input.hasAttribute('data-support-row-sku') ||
          input.hasAttribute('data-support-row-file-json') ||
          input.name === 'invoice_file_json';
        if (!isDynamicHidden) return;
        input.value = '';
      } else if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = input.defaultChecked;
      } else {
        input.value = '';
      }
      // Re-fires cascade filtering (product type → category/issue),
      // purchase-source "Others" toggle, and the installation pincode
      // serviceability gate — all already listen for these native events.
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });

    formRoot.querySelectorAll('.atomberg-support-field__error').forEach(function (el) {
      /** @type {HTMLElement} */ (el).hidden = true;
      el.textContent = '';
    });
    var checkingEl = formRoot.querySelector('[data-support-address-checking]');
    if (checkingEl) /** @type {HTMLElement} */ (checkingEl).hidden = true;

    showFormError(formRoot, '');
  }

  /** @param {HTMLElement} formRoot */
  function bindRetry(formRoot) {
    var retryBtn = formRoot.querySelector('[data-support-form-retry]');
    if (!retryBtn) return;

    retryBtn.addEventListener('click', function () {
      resetFormFields(formRoot);

      var resultEl = /** @type {HTMLElement|null} */ (
        formRoot.querySelector('[data-support-form-result]')
      );
      if (resultEl) resultEl.hidden = true;

      formRoot.querySelectorAll('[data-support-form-step]').forEach(function (step, i) {
        /** @type {HTMLElement} */ (step).hidden = i !== 0;
      });

      var submitBtn = /** @type {HTMLButtonElement|null} */ (
        formRoot.querySelector('[data-support-form-submit]')
      );
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
      }
    });
  }

  /** @param {HTMLElement} formRoot */
  function bindSubmit(formRoot) {
    var submitBtnEl = /** @type {HTMLButtonElement|null} */ (
      formRoot.querySelector('[data-support-form-submit]')
    );
    var formType = formRoot.getAttribute('data-support-form-type') || '';
    if (!submitBtnEl || !formType) return;
    var submitBtn = submitBtnEl;

    submitBtn.addEventListener('click', function () {
      var visibleStep = /** @type {HTMLElement|null} */ (
        formRoot.querySelector('[data-support-form-step]:not([hidden])')
      );
      if (visibleStep && !showFieldErrors(visibleStep)) {
        showFormError(formRoot, '');
        return;
      }
      // data-support-otp-optional forms (Register Warranty) allow submitting
      // unverified — the user explicitly skipped OTP and entered details
      // manually; the payload carries otp_verified:false for the backend.
      var otpOptional = formRoot.getAttribute('data-support-otp-optional') === 'true';
      if (!isOtpVerified(formRoot) && !otpOptional) {
        showFormError(formRoot, 'Please verify your mobile number before submitting.');
        return;
      }
      var submitGate = installationGateMessage(formRoot);
      if (submitGate) {
        showFormError(formRoot, submitGate);
        return;
      }
      var repeatableRoot = formRoot.querySelector('[data-support-repeatable]');
      if (
        repeatableRoot &&
        repeatableRoot.getAttribute('data-support-repeatable-invalid') === 'true'
      ) {
        showFormError(formRoot, 'Please verify the entered serial number(s)');
        return;
      }
      showFormError(formRoot, '');

      var apiBaseUrl = getApiBaseUrl();
      var payload = buildPayload(formRoot, formType);
      var endpoint = endpointFor(formType);

      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';

      fetch(apiBaseUrl + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (r) {
          // A 413 can arrive as an infra-level block with no JSON body at
          // all, or as the app's own body-parser limit with a raw
          // "request entity too large" string — neither is something a
          // customer should ever read verbatim, so both are replaced with
          // one actionable message naming the real configured file limit,
          // ahead of the generic JSON-parsing path below.
          if (r.status === 413) {
            return {
              status: false,
              message:
                'That file is too large to upload. Please choose a file under ' +
                getMaxFileSizeMb(formRoot) +
                'MB and try again.',
            };
          }
          // Read as text first, not r.json() directly — a structured error
          // body (any shape, {status:...} or NestJS's {success:...}) must
          // still reach showResult() so its real message displays instead
          // of a generic fallback; only a response that ISN'T valid JSON at
          // all (an infra-level 502/504 HTML page, an empty body — never a
          // deliberate backend error response) falls through to a message
          // that at least names the HTTP status, since there's no backend
          // message to show in that case.
          return r
            .text()
            .then(function (text) {
              try {
                return text ? JSON.parse(text) : null;
              } catch (_) {
                return null;
              }
            })
            .then(function (parsed) {
              if (parsed) return parsed;
              return {
                status: false,
                message:
                  'The server returned an unexpected response (HTTP ' +
                  r.status +
                  '). Please try again.',
              };
            });
        })
        .catch(function () {
          // fetch() itself rejected — no response reached us at all (offline,
          // DNS failure, CORS block) — distinct from the case above, where a
          // response DID arrive but wasn't the JSON we expected.
          return {
            status: false,
            message: 'Could not reach the server. Check your connection and try again.',
          };
        })
        .then(function (res) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit';
          // Fan-TYPE check, not category-contains-ceiling: the SMS
          // confirmation flow covers every fan installation (pedestal, wall,
          // exhaust too), while Water Purifier / Smart Lock keep the
          // standard message.
          var hasFan =
            formType === 'installation' &&
            Array.isArray(payload.items) &&
            payload.items.some(function (/** @type {{product_type?: string}} */ item) {
              return ((item.product_type || '') + '').toLowerCase() === 'fan';
            });
          // A refused identity token surfaces as the generic OTP message, which a
          // signed-in customer cannot act on — they were never shown an OTP step.
          // The token is stamped at page render and ages out, so the real fix is a
          // reload. Backstop only: atomberg-support-otp.js warns before this point.
          if (
            !res.status &&
            payload.customer_token &&
            /verification required/i.test(res.message || '')
          ) {
            res.message = 'This page has been open too long. Please refresh it and submit again.';
          }
          showResult(formRoot, res, { fan: hasFan });
        });
    });
  }

  // The native date picker can't reach past min/max, but the keyboard can
  // TYPE any date — reject it the moment the field holds a complete
  // out-of-range value instead of letting it sit until the submit gate.
  ['input', 'change'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var el = /** @type {HTMLInputElement|null} */ (e.target);
      if (!el || !el.matches || !el.matches('[data-support-form-root] input[type="date"]')) {
        return;
      }
      var v = el.validity;
      if (!v.rangeOverflow && !v.rangeUnderflow) return;
      // Message computed BEFORE the wipe — clearing resets ValidityState.
      var message = fieldErrorMessage(el);
      var target = fieldErrorEl(el);
      el.value = '';
      if (target) {
        target.textContent = message;
        target.hidden = false;
      }
      el.setAttribute('aria-invalid', 'true');
    });
  });

  // Row templates clone AFTER the init pass below — repeatable.js re-runs
  // the marker decoration on each new row through this hook.
  w.AtombergSupport = w.AtombergSupport || {};
  w.AtombergSupport.markRequiredLabels = markRequiredLabels;

  function initAll() {
    document.querySelectorAll('[data-support-form-root]').forEach(function (el) {
      var root = /** @type {HTMLElement} */ (el);
      if (/** @type {any} */ (root)._supportFormBound) return;
      /** @type {any} */ (root)._supportFormBound = true;
      markRequiredLabels(root);
      bindFieldErrorClearing(root);
      bindContinueBack(root);
      bindSubmit(root);
      bindRetry(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
